// https://developer.mozilla.org/en-US/docs/Web/API/Selection

export interface SelectionPoint {
  node: Node;
  textOffset: number;
  afterNode?: Node;
}

export interface SelectionLookup {
  anchor?: SelectionPoint;
  focus?: SelectionPoint;
}

function selectionPointsAreEqual(a: SelectionPoint, b: SelectionPoint): boolean
{
  return (a && b) && a.node === b.node && a.textOffset === b.textOffset;
}

function makeSelectionPoint(node: Node, offset: number): SelectionPoint
{
  if (node.nodeName === '#text') {
    return {
      node: node,
      textOffset: offset,
    };
  } else {
    return (
      offset >= node.childNodes.length
        ? {
          node: undefined,
          textOffset: 0,
          afterNode: node.childNodes[offset - 1],
        }
        : {
          node: node.childNodes[offset],
          textOffset: 0,
        }
    );
  }
}

export function makeSelectionLookup(element: HTMLDivElement): SelectionLookup
{
  const rootNode = element.getRootNode() as Document | ShadowRoot;
  // Fallback, because `shadowRoot.getSelection` is non-standard API
  // not every browser supports it.
  const selection = 'getSelection' in rootNode
      ? rootNode.getSelection()
      : window.getSelection();
  const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : undefined;

  const anchor = range &&
    makeSelectionPoint(range.startContainer, range.startOffset);

  const focus = range &&
    makeSelectionPoint(range.endContainer, range.endOffset);

  return {
    anchor: anchor,
    focus: selectionPointsAreEqual(anchor, focus) ? undefined : focus,
  };
}
